// src/lib/firebase.ts
import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  User, 
  onAuthStateChanged,
  Auth
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
  Unsubscribe,
  Firestore
} from 'firebase/firestore';
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { AppData, FamilyMember, FamilyInvite, FamilyMetadata } from '../utils/storage';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCMqQD4hAz_99S5r6BleMua2-0IQlwAjjo",
  authDomain: "star-collector-cc9c1.firebaseapp.com",
  projectId: "star-collector-cc9c1",
  storageBucket: "star-collector-cc9c1.firebasestorage.app",
  messagingSenderId: "113796904078",
  appId: "1:113796904078:web:7d41048c2d4a93ddf3c5df",
  measurementId: "G-BQ6HZHFRQP"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics for its side effects (page-view tracking). Guarded so it
// never throws in unsupported environments (e.g. SSR or the jsdom test runner).
isAnalyticsSupported()
  .then((supported) => {
    if (supported) {
      getAnalytics(app);
    }
  })
  .catch(() => {
    /* Analytics is optional; ignore initialization failures. */
  });

const auth = getAuth(app);

const db = getFirestore(app);

// Helper function to remove undefined values from an object
const removeUndefinedValues = (obj: unknown): unknown => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined);
  }
  
  const cleaned: Record<string, unknown> = {};
  Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined) {
      cleaned[key] = removeUndefinedValues(value);
    }
  });
  
  return cleaned;
};

// Helper function to analyze data for undefined values
const analyzeDataForUndefined = (obj: unknown, path = ''): string[] => {
  const undefinedPaths: string[] = [];
  
  const analyze = (value: unknown, currentPath: string) => {
    if (value === undefined) {
      undefinedPaths.push(currentPath);
      return;
    }
    
    if (value === null || typeof value !== 'object') {
      return;
    }
    
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        analyze(item, `${currentPath}[${index}]`);
      });
    } else {
      Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
        const newPath = currentPath ? `${currentPath}.${key}` : key;
        analyze(val, newPath);
      });
    }
  };
  
  analyze(obj, path);
  return undefinedPaths;
};

export class FirebaseService {
  private auth: Auth;
  private db: Firestore;
  private user: User | null = null;

  constructor() {
    this.auth = auth;
    this.db = db;
    
    // Listen for auth state changes
    onAuthStateChanged(this.auth, (user) => {
      this.user = user;
    });
  }

  /**
   * Get Firebase auth instance
   */
  getAuth(): Auth {
    return this.auth;
  }

  /**
   * Get Firestore instance
   */
  getDb(): Firestore {
    return this.db;
  }

  /**
   * Sign in with Google
   * @returns Promise with the signed-in user or null if sign-in failed
   */
  async signInWithGoogle(): Promise<User | null> {
    try {
      const provider = new GoogleAuthProvider();
      
      const result = await signInWithPopup(this.auth, provider);
      
      this.user = result.user;
      return result.user;
    } catch (error) {
      const firebaseError = error as { code?: string; message?: string; stack?: string };
      console.error('[firebase.ts] signInWithGoogle - Error during sign in:', {
        error: error,
        code: firebaseError.code || 'no code',
        message: firebaseError.message || 'no message',
        stack: firebaseError.stack || 'no stack'
      });
      return null;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    try {
      await this.auth.signOut();
      this.user = null;
    } catch (error) {
      const firebaseError = error as { code?: string; message?: string; stack?: string };
      console.error('[firebase.ts] signOut - Error during sign out:', {
        error: error,
        code: firebaseError.code || 'no code',
        message: firebaseError.message || 'no message',
        stack: firebaseError.stack || 'no stack'
      });
    }
  }

  /**
   * Check if a user is currently signed in
   * @returns boolean indicating if user is signed in
   */
  isUserSignedIn(): boolean {
    const isSignedIn = this.user !== null;
    return isSignedIn;
  }

  /**
   * Get the current user
   * @returns User or null if not signed in
   */
  getUser(): User | null {
    return this.user;
  }

  /**
   * Get the current user's ID
   * @returns User ID string or null if not signed in
   */
  getUserId(): string | null {
    const userId = this.user ? this.user.uid : null;
    return userId;
  }

  /**
   * Save app data to Firestore
   * @param data AppData to save
   * @returns Promise indicating success
   */
  async saveData(data: AppData): Promise<boolean> {

    // Analyze data for undefined values BEFORE processing
    const undefinedPaths = analyzeDataForUndefined(data);
    if (undefinedPaths.length > 0) {
      console.warn('[firebase.ts] saveData - FOUND UNDEFINED VALUES at paths:', undefinedPaths);
      undefinedPaths.forEach(path => {
        console.warn(`[firebase.ts] saveData - Undefined value at: ${path}`);
      });
    }

    if (!this.user) {
      console.error('[firebase.ts] saveData - Cannot save data: User not signed in');
      return false;
    }

    try {
      // Clean the data first to remove any undefined values
      const cleanedData = removeUndefinedValues(data);

      // Add timestamp to the cleaned data
      const dataWithTimestamp = {
        ...cleanedData,
        lastSync: new Date().toISOString()
      };

      // Verify no undefined values remain
      const undefinedPathsAfterCleaning = analyzeDataForUndefined(dataWithTimestamp);
      if (undefinedPathsAfterCleaning.length > 0) {
        console.error('[firebase.ts] saveData - STILL FOUND UNDEFINED VALUES after cleaning at paths:', undefinedPathsAfterCleaning);
        undefinedPathsAfterCleaning.forEach(path => {
          console.error(`[firebase.ts] saveData - Undefined value after cleaning at: ${path}`);
        });
      }

      // Check if user belongs to a family
      const userDoc = await getDoc(doc(this.db, "users", this.user.uid));
      const familyId = userDoc.data()?.familyId;

      // If user is in a family, save to family document instead
      if (familyId) {
        console.log('[Firebase] Saving to family:', familyId);
        return await this.saveFamilyData(familyId, dataWithTimestamp as AppData);
      }

      // Otherwise, save to user's individual document (legacy)
      const docRef = doc(this.db, "users", this.user.uid);
      await setDoc(docRef, dataWithTimestamp);

      return true;
    } catch (error) {
      const firebaseError = error as { code?: string; message?: string; stack?: string };
      console.error('[firebase.ts] saveData - Error saving data:', {
        error: error,
        code: firebaseError.code || 'no code',
        message: firebaseError.message || 'no message',
        stack: firebaseError.stack || 'no stack',
        user: this.user ? {
          uid: this.user.uid,
          email: this.user.email
        } : 'no user'
      });
      return false;
    }
  }

  /**
   * Load app data from Firestore
   * @returns Promise with the loaded AppData or null if not found
   */
  async loadData(): Promise<AppData | null> {

    if (!this.user) {
      console.error('[firebase.ts] loadData - Cannot load data: User not signed in');
      return null;
    }

    try {
      // First check if user belongs to a family
      const userDoc = await getDoc(doc(this.db, "users", this.user.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      const familyId = userData?.familyId;

      // If user has a familyId, load family data instead of user data
      if (familyId) {
        console.log('[Firebase] User belongs to family:', familyId);
        return await this.loadFamilyData(familyId);
      }

      // Otherwise, load user's individual data (legacy)
      const docSnap = await getDoc(doc(this.db, "users", this.user.uid));

      if (docSnap.exists()) {
        const data = docSnap.data() as AppData;
        return data;
      } else {
        return null;
      }
    } catch (error) {
      const firebaseError = error as { code?: string; message?: string; stack?: string };
      console.error('[firebase.ts] loadData - Error loading data:', {
        error: error,
        code: firebaseError.code || 'no code',
        message: firebaseError.message || 'no message',
        stack: firebaseError.stack || 'no stack',
        user: this.user ? {
          uid: this.user.uid,
          email: this.user.email
        } : 'no user'
      });
      return null;
    }
  }

  /**
   * Create a new family (user becomes the owner/host)
   * @param initialData Initial app data for the family
   * @returns Promise with the familyId
   */
  async createFamily(initialData: AppData): Promise<string> {
    if (!this.user) {
      throw new Error("Cannot create family: User not signed in");
    }

    try {
      const familyId = this.generateFamilyId();
      const now = new Date().toISOString();

      const familyMetadata: FamilyMetadata = {
        id: familyId,
        owner: this.user.uid,
        createdAt: now
      };

      const ownerMember: FamilyMember = {
        userId: this.user.uid,
        role: 'owner',
        displayName: this.user.displayName || 'Owner',
        email: this.user.email || '',
        photoURL: this.user.photoURL || undefined,
        joinedAt: now
      };

      const familyData: AppData = {
        ...initialData,
        familyId,
        familyMetadata,
        familyMembers: {
          [this.user.uid]: ownerMember
        },
        familyInvites: {},
        lastSync: now
      };

      await setDoc(doc(this.db, "families", familyId), familyData);

      await setDoc(doc(this.db, "users", this.user.uid), {
        familyId,
        role: 'owner',
        joinedAt: now
      });

      console.log('[Firebase] Family created:', familyId);
      return familyId;
    } catch (error) {
      console.error("Error creating family:", error);
      throw error;
    }
  }

  async generateInviteCode(): Promise<string> {
    if (!this.user) {
      throw new Error("Cannot generate invite: User not signed in");
    }

    try {
      const userDoc = await getDoc(doc(this.db, "users", this.user.uid));
      const familyId = userDoc.data()?.familyId;

      if (!familyId) {
        throw new Error("User does not belong to a family");
      }

      const inviteCode = this.generateShortCode();
      const now = new Date().toISOString();

      const invite: FamilyInvite = {
        code: inviteCode,
        createdBy: this.user.uid,
        createdAt: now,
        status: 'active'
      };

      const familyRef = doc(this.db, "families", familyId);

      await updateDoc(familyRef, {
        [`familyInvites.${inviteCode}`]: invite,
        lastSync: now
      });

      await setDoc(doc(this.db, "invites", inviteCode), {
        familyId,
        createdBy: this.user.uid,
        createdAt: now,
        status: 'active'
      });

      console.log('[Firebase] Invite code generated:', inviteCode);
      return inviteCode;
    } catch (error) {
      console.error("Error generating invite code:", error);
      throw error;
    }
  }

  async joinFamily(inviteCode: string): Promise<string> {
    if (!this.user) {
      throw new Error("Cannot join family: User not signed in");
    }

    try {
      const familyId = await this.findFamilyByInviteCode(inviteCode);

      if (!familyId) {
        throw new Error("Invalid invite code");
      }

      const familyRef = doc(this.db, "families", familyId);
      const familySnap = await getDoc(familyRef);

      if (!familySnap.exists()) {
        throw new Error("Family not found");
      }

      const familyData = familySnap.data() as AppData;
      const invite = familyData.familyInvites?.[inviteCode];

      if (!invite || invite.status !== 'active') {
        throw new Error("Invite code is invalid or has been used");
      }

      const now = new Date().toISOString();

      const newMember: FamilyMember = {
        userId: this.user.uid,
        role: 'parent',
        displayName: this.user.displayName || 'Parent',
        email: this.user.email || '',
        photoURL: this.user.photoURL || undefined,
        joinedAt: now
      };

      await updateDoc(familyRef, {
        [`familyMembers.${this.user.uid}`]: newMember,
        [`familyInvites.${inviteCode}.status`]: 'used',
        [`familyInvites.${inviteCode}.usedBy`]: this.user.uid,
        [`familyInvites.${inviteCode}.usedAt`]: now,
        lastSync: now
      });

      await setDoc(doc(this.db, "users", this.user.uid), {
        familyId,
        role: 'parent',
        joinedAt: now
      });

      console.log('[Firebase] Joined family:', familyId);
      return familyId;
    } catch (error) {
      console.error("Error joining family:", error);
      throw error;
    }
  }

  async revokeInvite(inviteCode: string): Promise<void> {
    if (!this.user) {
      throw new Error("Cannot revoke invite: User not signed in");
    }

    try {
      const userDoc = await getDoc(doc(this.db, "users", this.user.uid));
      const familyId = userDoc.data()?.familyId;

      if (!familyId) {
        throw new Error("User does not belong to a family");
      }

      const familyRef = doc(this.db, "families", familyId);
      const now = new Date().toISOString();

      await updateDoc(familyRef, {
        [`familyInvites.${inviteCode}.status`]: 'revoked',
        lastSync: now
      });

      await updateDoc(doc(this.db, "invites", inviteCode), {
        status: 'revoked'
      });

      console.log('[Firebase] Invite revoked:', inviteCode);
    } catch (error) {
      console.error("Error revoking invite:", error);
      throw error;
    }
  }

  async loadFamilyData(familyId: string): Promise<AppData | null> {
    try {
      const docSnap = await getDoc(doc(this.db, "families", familyId));
      if (docSnap.exists()) {
        return docSnap.data() as AppData;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error loading family data:", error);
      return null;
    }
  }

  async saveFamilyData(familyId: string, data: AppData): Promise<boolean> {
    try {
      const dataWithTimestamp = {
        ...data,
        lastSync: new Date().toISOString()
      };

      await setDoc(doc(this.db, "families", familyId), dataWithTimestamp);
      return true;
    } catch (error) {
      console.error("Error saving family data:", error);
      return false;
    }
  }

  listenToFamilyData(familyId: string, callback: (data: AppData) => void): Unsubscribe {
    const familyRef = doc(this.db, "families", familyId);

    return onSnapshot(familyRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as AppData;
        callback(data);
      }
    }, (error) => {
      console.error("Error listening to family data:", error);
    });
  }

  async getUserFamilyId(): Promise<string | null> {
    if (!this.user) {
      return null;
    }

    try {
      const userDoc = await getDoc(doc(this.db, "users", this.user.uid));
      return userDoc.data()?.familyId || null;
    } catch (error) {
      console.error("Error getting user family ID:", error);
      return null;
    }
  }

  private generateFamilyId(): string {
    return `fam_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private generateShortCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  private async findFamilyByInviteCode(inviteCode: string): Promise<string | null> {
    try {
      const inviteDoc = await getDoc(doc(this.db, "invites", inviteCode));
      if (inviteDoc.exists()) {
        return inviteDoc.data().familyId;
      }
      return null;
    } catch (error) {
      console.error("Error finding family by invite code:", error);
      return null;
    }
  }
}

// Create and export a singleton instance
export const firebaseService = new FirebaseService();
